/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { Box, Card, Group, HoverCard, HoverCardProps, Loader } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useDisclosure } from '@mantine/hooks'
import Amount from '../transaction/Amount'
import Ref from '../Ref'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { FormattedDate, useIntl } from 'react-intl'
import FurnishingAndEquipmentLabel from '../labels/FurnishingAndEquipmentLabel'
import { fetchLease } from '../../lib/rest/leaseAPIClient'
import { getLeaseLink } from '../../lib/links/leaseLinks'
import EstatePhoto from '../estate/EstatePhoto'
import LabelledValue from '../LabelledValue'
import { getCustomerName } from '../../lib/customer.mjs'
import { getCustomerLink } from '../../lib/links/customerLinks'

export type LeaseInfoHoverCardProps = HoverCardProps & {
  leaseId: number,
}

function LeaseInfoHoverCard (props: LeaseInfoHoverCardProps) {
  const {
    children,
    leaseId,
    ...others
  } = props

  const intl = useIntl()
  const [opened, { toggle }] = useDisclosure()

  const leaseQuery = useQuery({
    enabled: leaseId != null && opened,
    queryKey: ['lease', leaseId],
    queryFn: () => fetchLease(leaseId),
    refetchOnWindowFocus: false
  })
  const lease = leaseQuery.data

  return (
    <HoverCard
      openDelay={500}
      closeDelay={200}
      onOpen={toggle}
      onClose={toggle}
      position="top"
      {...others}
    >
      <HoverCard.Target>
        {children}
      </HoverCard.Target>

      <HoverCard.Dropdown maw={400} p={leaseQuery.isLoading ? undefined : 0}>
        {leaseQuery.isLoading
          ? <Loader size="sm" />
          : null}

        {lease != null
          ? <Card>
            <Card.Section>
              <EstatePhoto
                height={150}
                photos={lease.Estate?.Photos}
              />
            </Card.Section>

            <Box mt="md">
              <Group gap="xs">
                <AnchorLink to={getLeaseLink(leaseId)}>
                  <Ref fw="bold" value={lease.reference} />
                </AnchorLink>
              </Group>

              <Box fz="sm">
                {lease.rent
                  ? <LabelledValue
                    label={intl.formatMessage({
                      id: 'rent',
                      defaultMessage: 'Loyer'
                    })}
                    value={<Amount inherit span value={lease.rent} withCurrency />}
                  />
                  : null}

                {lease.Lessor
                  ? <LabelledValue
                    label={intl.formatMessage({
                      id: 'lessor',
                      defaultMessage: 'Bailleur'
                    })}
                    value={<AnchorLink to={getCustomerLink(lease.lessorId)}>
                      {getCustomerName(lease.Lessor)}
                    </AnchorLink>}
                  />
                  : null}

                {lease.Tenant
                  ? <LabelledValue
                    label={intl.formatMessage({
                      id: 'tenant',
                      defaultMessage: 'Locataire'
                    })}
                    value={<AnchorLink to={getCustomerLink(lease.tenantId)}>
                      {getCustomerName(lease.Tenant)}
                    </AnchorLink>}
                  />
                  : null}

                <LabelledValue
                  label={intl.formatMessage({
                    id: 'start',
                    defaultMessage: 'Début'
                  })}
                  value={<FormattedDate value={lease.startDate} />}
                />

                {lease.endDate
                  ? <LabelledValue
                    label={intl.formatMessage({
                      id: 'end',
                      defaultMessage: 'Fin'
                    })}
                    value={<FormattedDate value={lease.endDate} />}
                  />
                  : null}

                <FurnishingAndEquipmentLabel
                  furnished={lease.furnished}
                  equipped={lease.equipped}
                />
              </Box>

            </Box>
          </Card>
          : null}
      </HoverCard.Dropdown>
    </HoverCard>
  )
}

export default LeaseInfoHoverCard
